import type { UnitSystem } from 'fitify-types/src/types/common'
import type { User } from 'fitify-types/src/types/user'
import type { UserLocation } from 'fitify-ui/src/server/types/user-location'

export enum Funnel {
  CHF01 = 'chf01',
  CHM01 = 'chm01',
  F01 = 'f01',
  GYMF01 = 'gymf01',
  HOPIF01 = 'hopif01',
  M01 = 'm01',
  MILICAF01 = 'milicaf01',
  MILICAM01 = 'milicam01',
  TCHF01 = 'tchf01',
  TCHM01 = 'tchm01',
  WALLPIF01 = 'wallpif01',
}

export enum OnboardingVariant {
  Mixed = 'mixed',
}

export const DID_YOU_KNOW_PLACEMENTS = [
  'none',
  'motivation_early',
  'life_events_stopped',
] as const

export type DidYouKnowPlacement = (typeof DID_YOU_KNOW_PLACEMENTS)[number]

export const ELIGIBILITY_PITCH_VARIANTS = [
  'none',
  'eligible_activity',
  'confident_activity',
  'eligible_almost_there',
  'confident_almost_there',
] as const

export type EligibilityPitchVariant =
  (typeof ELIGIBILITY_PITCH_VARIANTS)[number]

type FunnelState<TData extends Record<string, unknown>> = {
  age?: number
  displayName?: string
  email?: string
  gender?: User.Profile.Gender
  goal?: User.Profile.Goals
  goal_weight?: number
  height?: number
  latest_ideal_weight?: User.Profile.LatestIdealWeight
  life_event?: User.Profile.LifeEvent
  max_impact?: User.Profile.MaxImpact
  problem_areas?: User.Profile.ProblemArea[]
  units?: UnitSystem
  water_intake?: User.Profile.WaterIntake
  weight?: number
  workout_length?: User.Profile.WorkoutLength
} & TData

type F01FunnelState = FunnelState<{
  age_bucket?: Question.AgeBucket
  body_goal?: Question.BodyGoal
  cravings?: Question.Cravings[]
  current_shape?: User.Profile.CurrentShape
  diet?: Question.Diet
  energy_levels?: Question.EnergyLevels
  exercise_barrier?: Question.ExerciseBarrier
  experience?: Question.YesNo
  family_diseases?: Question.YesNo
  fitness_level_crunches?: User.Profile.FitnessLevelCrunches
  fitness_level_squats?: User.Profile.FitnessLevelSquats
  fitness_level_plank?: User.Profile.FitnessLevelPlank
  flexible?: Question.Flexible
  habits?: Question.Habits[]
  how_often_exercise?: Question.HowOftenExercise
  how_often_walks?: Question.HowOftenWalks
  life_event_date?: string
  life_events_stopped?: Question.LifeEventsStopped[]
  main_motivation?: Question.MainMotivation
  meal_times?: Question.YesNo
  motivation_early?: Question.MotivationEarly[]
  post_lunch_slump?: Question.YesNo
  sleep_duration?: Question.SleepDuration
  stairs_cardio?: Question.StairsCardio
  stress?: User.Profile.Stress
  struggle?: Question.Struggle[]
  time_breakfast?: Question.TimeBreakfast
  time_dinner?: Question.TimeDinner
  time_lunch?: Question.TimeLunch
  typical_day_activity?: Question.TypicalDayActivity
  weight_change_exp?: Question.WeightChangeExp
  work_schedule?: Question.WorkSchedule
  workout_duration?: number
}>

type GymSectionState = {
  equipment_preference?: Question.EquipmentPreference
  gym_confidence?: Question.GymConfidence
  gym_difficulty?: Question.GymDifficulty
  gym_type?: Question.GymType
  home_difficulty?: Question.HomeDifficulty
  home_gym?: Question.HomeGym
  workout_location?: Question.WorkoutLocation
}

type GymF01FunnelState = F01FunnelState & GymSectionState

type M01FunnelState = FunnelState<{
  age_bucket?: Question.AgeBucket
  alcohol?: Question.Alcohol
  body_goal?: Question.BodyGoal
  current_shape?: User.Profile.CurrentShape
  cravings?: Question.Cravings[]
  diet?: Question.Diet
  energy_levels?: Question.EnergyLevels
  exercise_barrier?: Question.ExerciseBarrier
  experience?: Question.YesNo
  family_diseases?: Question.YesNo
  fitness_level_push_ups?: User.Profile.FitnessLevelPushUps
  fitness_level_squats?: User.Profile.FitnessLevelSquats
  fitness_level_plank?: User.Profile.FitnessLevelPlank
  flexible?: Question.Flexible
  habits?: Question.Habits[]
  how_often_exercise?: Question.HowOftenExercise
  how_often_walks?: Question.HowOftenWalks
  life_event_date?: string
  life_events_stopped?: Question.LifeEventsStopped[]
  meal_times?: Question.YesNo
  motivation_early?: Question.MotivationEarly[]
  post_lunch_slump?: Question.YesNo
  stress?: User.Profile.Stress
  struggle?: Question.Struggle[]
  sleep_duration?: Question.SleepDuration
  typical_day_activity?: Question.TypicalDayActivity
  smoking?: Question.Smoking
  weight_change_exp?: Question.WeightChangeExp
  workout_days?: User.Profile.WorkoutDays[]
  workout_duration?: number
  workout_frequency?: User.Profile.WorkoutFrequency
  work_schedule?: Question.WorkSchedule
}>

type CHM01FunnelState = FunnelState<{
  age_bucket?: Question.AgeBucket
  alcohol?: Question.Alcohol
  body_goal?: Question.BodyGoal
  cravings?: Question.Cravings[]
  current_shape?: User.Profile.CurrentShape
  diet?: Question.Diet
  energy_levels?: Question.EnergyLevels
  exercise_barrier?: Question.ExerciseBarrier
  experience?: Question.YesNo
  family_diseases?: Question.YesNo
  flexibility?: Question.Flexibility
  habits?: Question.Habits[]
  how_often_exercise?: Question.HowOftenExercise
  how_often_walks?: Question.HowOftenWalks
  life_event_date?: string
  life_events_stopped?: Question.LifeEventsStopped[]
  meal_times?: Question.YesNo
  motivation_early?: Question.MotivationEarly[]
  post_lunch_slump?: Question.YesNo
  sleep_duration?: Question.SleepDuration
  smoking?: Question.Smoking
  stairs_cardio?: Question.StairsCardio
  stress?: User.Profile.Stress
  struggle?: Question.Struggle[]
  typical_day_activity?: Question.TypicalDayActivity
  weight_change_exp?: Question.WeightChangeExp
  work_schedule?: Question.WorkSchedule
  workout_days?: User.Profile.WorkoutDays[]
  workout_duration?: number
  workout_frequency?: User.Profile.WorkoutFrequency
}>

type CHF01FunnelState = FunnelState<{
  age_bucket?: Question.AgeBucket
  body_goal?: Question.BodyGoal
  cravings?: Question.Cravings[]
  current_shape?: User.Profile.CurrentShape
  diet?: Question.Diet
  energy_levels?: Question.EnergyLevels
  exercise_barrier?: Question.ExerciseBarrier
  experience?: Question.YesNo
  family_diseases?: Question.YesNo
  flexibility?: Question.Flexibility
  habits?: Question.Habits[]
  how_often_exercise?: Question.HowOftenExercise
  how_often_walks?: Question.HowOftenWalks
  life_event_date?: string
  life_events_stopped?: Question.LifeEventsStopped[]
  main_motivation?: Question.MainMotivation
  meal_times?: Question.YesNo
  motivation_early?: Question.MotivationEarly[]
  post_lunch_slump?: Question.YesNo
  sleep_duration?: Question.SleepDuration
  stairs_cardio?: Question.StairsCardio
  stress?: User.Profile.Stress
  struggle?: Question.Struggle[]
  time_breakfast?: Question.TimeBreakfast
  time_dinner?: Question.TimeDinner
  time_lunch?: Question.TimeLunch
  typical_day_activity?: Question.TypicalDayActivity
  weight_change_exp?: Question.WeightChangeExp
  work_schedule?: Question.WorkSchedule
  workout_duration?: number
}>

type DiscountRevealState = {
  discountPercentageValue?: number
  hasBeenShown?: boolean
}

export type DataState = {
  common: {
    didYouKnowPlacement?: DidYouKnowPlacement
    discount_reveal_state?: DiscountRevealState
    eligibilityPitchVariant?: EligibilityPitchVariant
    onboardingVariant?: OnboardingVariant
    printableId?: string
    subscriptionFunnel?: Funnel
    user_location?: {
      isLoaded: boolean
      data?: UserLocation
    }
  }
  [Funnel.CHF01]: CHF01FunnelState
  [Funnel.CHM01]: CHM01FunnelState
  [Funnel.F01]: F01FunnelState
  [Funnel.GYMF01]: GymF01FunnelState
  [Funnel.HOPIF01]: F01FunnelState
  [Funnel.M01]: M01FunnelState
  [Funnel.MILICAF01]: F01FunnelState
  [Funnel.MILICAM01]: M01FunnelState
  [Funnel.TCHF01]: CHF01FunnelState
  [Funnel.TCHM01]: CHM01FunnelState
  [Funnel.WALLPIF01]: F01FunnelState
  shared: {
    age_bucket?: Question.AgeBucket
    email?: string
    gender?: User.Profile.Gender
    has_subscription?: boolean
  }
}

export type DataPath = keyof DataState
export type FunnelDataPath = Exclude<DataPath, 'common'>
export type DataPathState<TPath extends DataPath> = DataState[TPath]

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Question {
  export enum Flexible {
    Pretty = 'pretty',
    Starting = 'starting',
    NotGood = 'not_good',
    NotSure = 'not_sure',
  }

  export enum Struggle {
    Back = 'back',
    Knees = 'knees',
    None = 'none',
  }

  export enum MotivationEarly {
    Strength = 'strength',
    Posture = 'posture',
    Stress = 'stress',
    Flexibility = 'flexibility',
    None = 'none',
    LookBetter = 'look_better',
    GetStronger = 'get_stronger',
    FeelBetterAndHealthier = 'feel_better_healthier',
    MoreTimeWithLovedOnes = 'more_time_with_loved_ones',
    Other = 'other',
    Stiffness = 'stiffness',
    Balance = 'balance',
    Energy = 'energy',
  }

  export enum BodyGoal {
    Thin = 'thin',
    Toned = 'toned',
    Curvy = 'curvy',
    Average = 'average',
    BitLeaner = 'bit_leaner',
    Athletic = 'athletic',
    Shredded = 'shredded',
    ExtraBulky = 'extra_bulky',
  }

  export enum WeightChangeExp {
    EasyGainSlowLoss = 'easy_gain_slow_loss',
    EasyGainEasyLoss = 'easy_gain_easy_loss',
    HardGain = 'hard_gain',
  }

  export enum AgeBucket {
    Age_18_29 = '18_29',
    Age_18_39 = '18_39',
    Age_30_39 = '30_39',
    Age_40_49 = '40_49',
    Age_50_plus = '50_plus',
    Age_50_59 = '50_59',
    Age_60_plus = '60_plus',
    Age_60_69 = '60_69',
    Age_70_plus = '70_plus',
  }

  export enum WorkSchedule {
    NineToFive = 'nine_to_five',
    Night = 'night',
    Flexible = 'flexible',
    NotWorking = 'not_working',
  }

  export enum TypicalDayActivity {
    Seated = 'seated',
    Balanced = 'balanced',
    OnFeet = 'on_feet',
  }

  export enum EnergyLevels {
    LowAllDay = 'low_all_day',
    DropAfterLunch = 'drop_after_lunch',
    DrainedBeforeMeals = 'drained_before_meals',
    High = 'high',
  }

  export enum SleepDuration {
    FiveOrLess = '5_less',
    FiveToSix = '5_6',
    SevenToEight = '7_8',
    EightOrMore = '8_more',
  }

  export enum StairsCardio {
    Challenging = 'challenging',
    Tiring = 'tiring',
    Easy = 'easy',
    NoProblem = 'no_problem',
  }

  export enum HowOftenExercise {
    Daily = 'daily',
    Weekly = 'weekly',
    Monthly = 'monthly',
    Never = 'never',
  }

  export enum HowOftenWalks {
    Daily = 'daily',
    Weekly = 'weekly',
    Monthly = 'monthly',
    Rarely = 'rarely',
  }

  export enum ExerciseBarrier {
    Time = 'time',
    Hard = 'hard',
    Unsure = 'unsure',
    Motivation = 'motivation',
    Other = 'other',
  }

  export enum TimeBreakfast {
    SixToEight = '6_8',
    EightToTen = '8_10',
    TenToNoon = '10_noon',
    SkipBreakfast = 'skip_breakfast',
  }

  export enum TimeLunch {
    TenToNoon = '10_noon',
    NoonToTwo = 'noon_2',
    TwoToFour = '2_4',
    SkipLunch = 'skip_lunch',
  }
  export enum TimeDinner {
    FourToSix = '4_6',
    SixToEight = '6_8',
    AfterEight = 'after_8',
    SkipDinner = 'skip_dinner',
  }

  export enum Diet {
    Traditional = 'traditional',
    Keto = 'keto',
    Paleo = 'paleo',
    Vegetarian = 'vegetarian',
    Vegan = 'vegan',
    Mediterranean = 'mediterranean',
    Pescatarian = 'pescatarian',
    LactoseFree = 'lactose_free',
    GlutenFree = 'gluten_free',
  }

  export enum Habits {
    EmotionalEating = 'emotional_eating',
    Overeating = 'overeating',
    LateSnacking = 'late_snacking',
    SkippingMeals = 'skipping_meals',
    None = 'none',
  }

  export enum Cravings {
    SweetFoods = 'sweet_foods',
    SaltySnacks = 'salty_snacks',
    FastFoodMeals = 'fast_food_meals',
    SodaOrSugaryDrinks = 'soda_or_sugary_drinks',
    None = 'none',
  }

  export enum Smoking {
    Yes = 'yes',
    Sometimes = 'sometimes',
    WhenDrinking = 'when_drinking',
    Quit = 'quit',
    Never = 'never',
  }

  export enum Alcohol {
    Weekly = 'weekly',
    Monthly = 'monthly',
    SpecialOccasions = 'special_occasions',
    Never = 'never',
  }

  export enum LifeEventsStopped {
    StressAtWork = 'stress_at_work',
    BusyFamilySchedule = 'busy_family_schedule',
    BreakupOrDivorce = 'breakup_or_divorce',
    MetabolismSlowing = 'metabolism_slowing',
    FinancialDifficulties = 'financial_difficulties',
    OtherStressfulEvents = 'other_stressful_events',
    None = 'none',
  }

  export enum MainMotivation {
    Confident = 'confident',
    Energy = 'energy',
    Clothes = 'clothes',
    AfterBaby = 'after_baby',
    Health = 'health',
    Other = 'other',
  }

  export enum YesNo {
    Yes = 'yes',
    No = 'no',
  }

  export enum Flexibility {
    Low = 'low',
    Medium = 'medium',
    High = 'high',
  }

  export enum WorkoutLocation {
    Gym = 'gym',
    HomeGym = 'home_gym',
    Both = 'both',
    DoNotKnow = 'do_not_know',
  }

  export enum GymType {
    FullGym = 'full_gym',
    BasicGym = 'basic_gym',
    DoNotKnow = 'do_not_know',
  }

  export enum EquipmentPreference {
    Machines = 'machines',
    FreeWeights = 'free_weights',
    Both = 'both',
    DoNotKnow = 'do_not_know',
  }

  export enum GymConfidence {
    NeverBeen = 'never_been',
    Unsure = 'unsure',
    KnowTheBasics = 'know_the_basics',
    Confident = 'confident',
  }

  export enum GymDifficulty {
    SlowProgress = 'slow_progress',
    NoPlan = 'no_plan',
    Costs = 'costs',
    Time = 'time',
    BoringRoutines = 'boring_routines',
    None = 'none',
  }

  export enum HomeGym {
    Garage = 'garage',
    BasicSetup = 'basic_setup',
    DoNotKnow = 'do_not_know',
  }

  export enum HomeDifficulty {
    SlowProgress = 'slow_progress',
    NoPlan = 'no_plan',
    Time = 'time',
    Motivation = 'motivation',
    BoringRoutines = 'boring_routines',
    None = 'none',
  }
}
